import { useGetEmployeeByUuid } from './useGetEmployeeByUuid';
import type { EmployeeDetailRequestDto } from '@personnel/application/dtos';

/**
 * Hook for accessing employee data within Personnel domain
 *
 * Eliminates prop drilling by accessing employee data through context.
 * Uses React Query for caching and automatic updates.
 * Query is automatically disabled when request is undefined.
 *
 * @param {EmployeeDetailRequestDto | undefined} request - Employee query parameters
 * @param {string} request.userUuid - Employee UUID to fetch
 * @param {boolean} [request.includeAssignedStores] - Whether to include assigned stores in response
 * @returns {Object} Query result with employee detail
 * @returns {EmployeeDetailDto} returns.data - Employee detail data
 * @returns {boolean} returns.isLoading - Whether employee is being fetched
 * @returns {boolean} returns.isError - Whether an error occurred
 * @returns {Error} returns.error - Error object if request failed
 *
 * @example
 * // Fetch employee with assigned stores
 * const { data: employee, isLoading } = usePersonnelEmployee({
 *   userUuid: employeeId,
 *   includeAssignedStores: true
 * });
 *
 * if (isLoading) return <Spinner />;
 *
 * return <EmployeeDetails employee={employee} />;
 *
 * @example
 * // Conditional query (disabled when no ID)
 * const employeeId = mode === 'edit' ? uuid : undefined;
 * const request = employeeId ? { userUuid: employeeId } : undefined;
 * const { data: employee } = usePersonnelEmployee(request);
 */
export const usePersonnelEmployee = (request: EmployeeDetailRequestDto | undefined) => {
  return useGetEmployeeByUuid(request);
};
